/**
 * Презентационные части подвкладки «Видео» (DcVideoStatsSection). Вынесено ради лимита
 * размера файла. Стиль — конституция DC: var(--*), цифры данных = var(--f-meta) tabular-nums.
 */
import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { useAvatarSrc } from "../../hooks/useAvatarSrc";
import { useVideoViews, type VideoTopItem } from "../../hooks/useVideoStats";

// Мелкое превью видео-момента (кадр-постер + значок play, фолбэк при битом постере).
export function VideoThumb({ url, w = 46, h = 30 }: { url: string; w?: number; h?: number }): ReactElement {
  const [failed, setFailed] = useState(false);
  return (
    <span style={{ position: "relative", width: w, height: h, borderRadius: 4, overflow: "hidden", background: "var(--surface-2)", border: "1px solid var(--surface-3)", display: "inline-grid", placeItems: "center", flex: "0 0 auto" }}>
      {url && !failed ? <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
      <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="rgba(255,255,255,.92)" style={{ filter: "drop-shadow(0 0 2px rgba(0,0,0,.7))" }}><path d="M8 5v14l11-7z" /></svg>
      </span>
    </span>
  );
}

export const NUM: CSSProperties = { fontFamily: "var(--f-meta)", fontVariantNumeric: "tabular-nums" };
export const box: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 10, background: "var(--surface-1)" };
export const capTitle: CSSProperties = { fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "var(--text-2)" };

export function todayBiz(): string { return new Date(Date.now() + 5 * 3600000).toISOString().slice(0, 10); }
export function shiftDate(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
export function fmt(n: number): string {
  if (!Number.isFinite(n)) return "0";
  const s = Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10).replace(".", ",");
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
export function fmtDm(iso: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : iso;
}
export function fmtWhen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const shifted = new Date(d.getTime() + 5 * 3600000);
  return `${String(shifted.getUTCDate()).padStart(2, "0")}.${String(shifted.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function AvatarImg({ url, name, size }: { url: string; name: string; size: number }): ReactElement {
  const { src, failed } = useAvatarSrc(url);
  const ch = (name || "?").trim().charAt(0).toUpperCase() || "?";
  const hue = Math.abs([...(name || "?")].reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7)) % 360;
  return (
    <span style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", display: "inline-grid", placeItems: "center", background: `hsl(${hue} 32% 26%)`, color: "#fff", fontSize: Math.round(size * 0.42), fontWeight: 800, flex: "0 0 auto" }}>
      {src && !failed ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : ch}
    </span>
  );
}

export function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }): ReactElement {
  return (
    <div style={{ ...box, padding: "6px 8px", minWidth: 0 }}>
      <div style={{ ...capTitle, fontSize: 9.5 }}>{label}</div>
      <div style={{ ...NUM, fontSize: 18, fontWeight: 800, color: "var(--text-1)", lineHeight: 1.1, marginTop: 2 }}>{value}</div>
      {sub ? <div style={{ ...NUM, fontSize: 10, color: "var(--text-3)" }}>{sub}</div> : null}
    </div>
  );
}

export function Bar({ label, count, max, hint }: { label: string; count: number; max: number; hint?: string }): ReactElement {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "84px 1fr auto", gap: 8, alignItems: "center", minHeight: 26 }}>
      <span style={{ fontSize: 12, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ position: "relative", height: 14, borderRadius: 3, background: "var(--surface-2)", overflow: "hidden" }}>
        <span style={{ position: "absolute", inset: 0, width: `${pct}%`, background: "color-mix(in srgb, var(--role) 40%, transparent)", borderRight: pct > 0 ? "1px solid var(--role)" : "none", boxShadow: pct > 0 ? "0 0 8px color-mix(in srgb, var(--role) 35%, transparent)" : "none" }} />
      </span>
      <span style={{ ...NUM, fontSize: 12, fontWeight: 700, color: "var(--text-1)", minWidth: 28, textAlign: "right" }}>{fmt(count)}{hint ? <span style={{ color: "var(--text-3)", fontWeight: 400 }}> {hint}</span> : null}</span>
    </div>
  );
}

// Модалка проигрывания видео-момента + все его данные.
export function VideoModal({ v, from, to, onClose }: { v: VideoTopItem; from?: string; to?: string; onClose: () => void }): ReactElement {
  const { views, loading } = useVideoViews(v.materialId, from, to);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const cell = (label: string, value: string): ReactElement => (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...capTitle, fontSize: 8.5 }}>{label}</div>
      <div style={{ ...NUM, fontSize: 13, fontWeight: 700, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
    </div>
  );
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 40, display: "grid", placeItems: "center", padding: 12, background: "rgba(4,5,7,.82)" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...box, width: "min(100%, 420px)", maxHeight: "90dvh", overflow: "auto", padding: 10, display: "grid", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.quest} · {fmtDm(v.gameDate)} {v.gameTime}</span>
          <button type="button" onClick={onClose} aria-label="Закрыть" style={{ flex: "0 0 auto", width: 26, height: 26, borderRadius: 6, border: "1px solid var(--surface-3)", background: "transparent", color: "var(--text-2)", cursor: "pointer" }}>✕</button>
        </div>
        <video controls autoPlay playsInline poster={v.posterUrl} src={v.streamUrl} style={{ width: "100%", borderRadius: 8, background: "#000", maxHeight: "56dvh" }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {cell("Показы", fmt(v.plays))}
          {cell("Зрители", fmt(v.viewers))}
          {cell("Минуты", fmt(v.watchMin))}
          {cell("Досмотр", `${fmt(v.completionRate)}%`)}
          {cell("Длина", v.duration || "—")}
          {cell("Оператор", v.operator || "—")}
        </div>
        <div style={{ display: "grid", gap: 4 }}>
          <div style={{ ...capTitle, fontSize: 9.5, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span>Каждый просмотр</span>
            <span style={{ ...NUM, color: "var(--text-3)", fontWeight: 400 }}>{fmt(views.length)}</span>
          </div>
          {loading ? <div style={{ padding: "6px 2px", color: "var(--text-3)", fontSize: 12 }}>Загрузка…</div>
            : views.length === 0 ? <div style={{ padding: "6px 2px", color: "var(--text-3)", fontSize: 12 }}>Просмотров за период нет</div>
              : <div style={{ display: "grid", gap: 0, maxHeight: 190, overflowY: "auto", border: "1px solid var(--surface-3)", borderRadius: 8 }}>
                {views.map((w, i) => (
                  <div key={w.ts + ":" + i} style={{ display: "grid", gridTemplateColumns: "24px 1fr auto", gap: 8, alignItems: "center", minHeight: 30, padding: "3px 8px", borderTop: i ? "1px solid var(--surface-3)" : "none" }}>
                    <AvatarImg url={w.avatarUrl} name={w.name || w.username || String(w.tg)} size={24} />
                    <span style={{ fontSize: 12.5, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.name || (w.username ? "@" + w.username : "id " + w.tg)}</span>
                    <span style={{ ...NUM, fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap" }}>{w.date} · {w.time}</span>
                  </div>
                ))}
              </div>}
        </div>
      </div>
    </div>
  );
}
